import { driveLinkToImgSrc, formatDistance, formatTimestamp, updateStore } from "../utils";
import { useModal } from "../context/ModalContext";
import { clockIcon, dangerIcon, discountFlag, discountSplash, doorIcon, hrefIcons, shareIcon } from "../assets/icons";
import { DEV, MENSAJE_REPORTE, NUMERO_REPORTE, SERVICE_TYPES, SERVICE_TYPE_CONFIG } from "../constants";
import { useLanguageContext } from "../context/LanguageContext";
import { useMapContext } from "../context/MapContext";
import { useEffect, useState } from "react";
import RedirectButton from "./ui/RedirectButton";

const AncorHelper = ({ children, condition = true, href = "#" }) => {
  return (
    condition && (
      <a
        href={href}
        target="_blank"
        className="mx-auto p-2 pl-4 flex flex-row items-center gap-2 text-gray-300 hover:text-green-600 cursor-pointer"
      >
        {children}
      </a>
    )
  );
};

const DivisoryLine = () => <div className="h-[2px] bg-gray-800 my-2" />;

export default function Modal() {
  const { t } = useLanguageContext();
  const { modal, hideModal } = useModal();
  const marker = modal.marker;
  const [act, updateAct] = useState(marker.activity || 3);
  const [actAmmount, updateActAmmount] = useState("");
  useEffect(() => {
    const x = formatTimestamp(marker.timestamp);
    updateAct(x[0]);
    updateActAmmount(x[1]);
  }, [marker]);
  if (!modal.isVisible) return null;
  const logo = driveLinkToImgSrc(marker.logo);

  function handleStoreUpdate() {
    marker.activity = 0;
    marker.timestamp = Date.now();
    updateAct(0);
    if (!DEV) {
      updateStore(marker);
    }
  }

  function getServiceTypeConfig(type) {
    return SERVICE_TYPE_CONFIG[type] ?? null;
  }

  function activityText({ act, actAmmount, t }) {
    if (act === 0) {
      return t("component", "modal", "today");
    }

    const config = ACTIVITY_CONFIG[act];
    const unitKey = actAmmount === 1 ? config.unit[0] : config.unit[1];

    return (
      `${t("component", "modal", "preText")} ` +
      `${actAmmount} ` +
      `${t("component", "modal", unitKey)}` +
      `${t("component", "modal", "afterText")}`
    );
  }

  function prettifyUrl(url) {
    try {
      const parsed = new URL(url);
      return parsed.hostname.replace(/^www\./, "");
    } catch {
      return url;
    }
  }
  function prettifyPhoneNumber(phone) {
    if (!phone) return "";

    const digits = phone.toString().replace(/\D/g, "");

    if (digits.length === 13 && digits.startsWith("549")) {
      return `+${digits.slice(0, 2)} 9 ${digits.slice(3, 5)} ${digits.slice(5, 9)}-${digits.slice(9)}`;
    }

    if (digits.length === 12 && digits.startsWith("54")) {
      return `+${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4, 8)}-${digits.slice(8)}`;
    }

    if (digits.length === 10) {
      return `${digits.slice(0, 2)} ${digits.slice(2, 6)}-${digits.slice(6)}`;
    }

    return digits.replace(/(\d{3})(?=\d)/g, "$1 ");
  }

  const ACTIVITY_CONFIG = {
    0: {
      color: "bg-status_green",
      textColor: "text-sub_text_color",
      label: (t) => t("component", "modal", "today"),
    },
    1: {
      color: "bg-status_green",
      textColor: "text-status_greenr",
      unit: ["day", "days"],
    },
    2: {
      color: "bg-status_yellow",
      textColor: "text-status_yellow",
      unit: ["month", "months"],
    },
    3: {
      color: "bg-status_red",
      textColor: "text-status_red",
      unit: ["year", "years"],
    },
  };

  return (
    <section className="bg-gray-900 w-full sm:w-96 h-screen absolute z-50 p-2">
      <button
        className="inline-flex items-center gap-1.5 h-8 px-2 rounded-md border border-slate-600 text-xs text-slate-200 hover:bg-slate-800 mt-2 ml-2"
        onClick={() => hideModal(marker)}
        aria-label="Mapa"
        title="Mapa"
      >
        <svg
          className="w-4 h-4"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M15 18l-6-6 6-6" />
        </svg>
        <span>{t("component", "sidebar", "map")}</span>
      </button>
      <div className="sm:h-28 sm:w-28 w-44 text-sub_text_color flex items-center justify-center h-[100px] w-[100px] relative mx-auto mt-4">
        {logo}
        {marker.discount ? (
          <div className="absolute -right-6 -bottom-4 h-16 w-16">
            {discountSplash}
            <span className="absolute top-4 right-3 text-white text-xs text-wrap h-8 w-10 flex items-center text-center justify-center font-semibold tracking-wider">
              {marker.discount != 1 ? marker.discount : ""}% OFF
            </span>
          </div>
        ) : (
          ""
        )}
      </div>

      <h1 className="text-lg py-3 text-white text-center">{marker.name}</h1>

      {marker.distance && <h2 className="text-gray-300 text-center text-sm">{formatDistance(marker.distance)}</h2>}

      <DivisoryLine />

      <div className="py-2 px-6 text-sm">
        <p className="pb-4 text-gray-300">{t("component", "categories", marker.category)}</p>

        <ul className="space-y-1">
          {marker.serviceType.map((type) => {
            const config = getServiceTypeConfig(type);
            if (!config) return null;

            return (
              <li key={type} className={`flex items-center gap-2 ${config.color}`}>
                {doorIcon}
                <span>{t("component", "marker", config.label)}</span>
              </li>
            );
          })}
        </ul>
      </div>

      <DivisoryLine />

      <div className="flex flex-row gap-4 items-center pl-6 text-sm text-gray-300 py-2">
        <h1>{t("component", "modal", "lastActivitySpan")}</h1>

        <div className="flex items-center gap-2">
          <span className={`h-3 w-3 rounded-full ${ACTIVITY_CONFIG[act]?.color}`} />

          <span className={ACTIVITY_CONFIG[act]?.textColor}>{activityText({ act, actAmmount, t })}</span>
        </div>
      </div>
      <button
        onClick={handleStoreUpdate}
        className="flex items-center mt-2 justify-center gap-2 mx-auto w-full rounded-lg bg-green-600 px-4 py-2 text-white text-sm font-medium shadow-md hover:bg-green-700 hover:shadow-lg"
      >
        <span className="h-4 w-4">{clockIcon}</span>
        <span>{t("component", "modal", "buttonUpdateLastActivity")}</span>
      </button>

      <DivisoryLine />

      <div className="p-2">
        <AncorHelper
          href={`https://www.google.com/maps/place/${marker.coords[1]},${marker.coords[0]}/@${marker.coords[1]},${marker.coords[0]}`}
        >
          <div className="w-6 h-4">{hrefIcons.location}</div>
          <span className="text-md">{`${marker.city} - ${marker.direction}`}</span>
        </AncorHelper>

        <AncorHelper condition={marker.phone} href={`tel:+${marker.phone}`}>
          {hrefIcons.phone}
          <span className="text-md">{prettifyPhoneNumber(marker.phone)}</span>
        </AncorHelper>

        <AncorHelper condition={marker.email} href={`mailto:${marker.email}`}>
          {hrefIcons.email}
          <span className="text-md">{marker.email}</span>
        </AncorHelper>

        <AncorHelper condition={marker.website} href={marker.website}>
          {hrefIcons.website}
          <span className="text-md">{prettifyUrl(marker.website)}</span>
        </AncorHelper>
      </div>

      <div className="h-[2px] bg-gray-800 my-6" />

      <RedirectButton
        title={t("component", "modal", "buttonReport")}
        primary={false}
        danger={true}
        target="_blank"
        href={`https://api.whatsapp.com/send?phone=${NUMERO_REPORTE}&text=${MENSAJE_REPORTE}${"%0aNombre: " + marker.name}${
          "%0aUbicacion: " + marker.city + " - " + marker.direction + "%0a"
        } `}
      />
    </section>
  );
}
